import { Component, ChangeDetectionStrategy } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { ThemeService } from './services/theme.service';
import { UsersService } from './services/users.service';
import { BreakpointObserver } from '@angular/cdk/layout';
import { filter } from 'rxjs/operators';
import { Title } from '@angular/platform-browser';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatDialog } from '@angular/material/dialog';
import { AfterViewInit, ViewChild } from '@angular/core';
import { Location } from '@angular/common';
import { forkJoin, of } from 'rxjs';
import { catchError } from 'rxjs/operators';

import { SplashScreenComponent } from './splash-screen/splash-screen.component';
import { PwaService } from './services/pwa.service';
import { AppInfoService } from './services/app-info.service';
import { AppInfoDialogComponent } from './shared/app-info-dialog/app-info-dialog.component';
import { LoggerService } from './services/logger.service';
import { ScrollPositionService } from './services/scroll-position.service';
import { MobileService } from './services/mobile.service';

declare let gtag: Function;

@Component({
  selector: 'app-root',
  standalone: false,
  templateUrl: './app.mobile.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./app.mobile.component.scss'],
})
export class AppMobileComponent implements AfterViewInit {
  @ViewChild('splashScreen') splashScreenComponent!: SplashScreenComponent;
  
  showGoTop = false;
  showSplashScreen = true; // Add splash screen control
  isLoadingData = false;
  loadingProgress = 0;
  minimumSplashTime = 6000; // Maximum 6 seconds for splash screen

  pageTitle = 'Strona startowa';
  pageIntro = 'Strona startowa';
  isLoggedIn: boolean = false;
  showInstallPwa = false;
  showGoBack = false;
  wcagMode = false; // WCAG accessibility mode
  deferredPrompt: any = null;
  private wasOffline = false;
  private connectivityCheckInterval: any = null;
  private lastIconsLoadedState: boolean | null = null;

  constructor(private router: Router, public themeService: ThemeService,
    private userService: UsersService,
    private mobileService: MobileService,
    private location: Location,
    private breakpointObserver: BreakpointObserver, private titleService: Title, private snackBar: MatSnackBar,
    private pwaService: PwaService, private dialog: MatDialog, private appInfoService: AppInfoService,
    private loggerService: LoggerService,
    private scrollPositionService: ScrollPositionService) {

           this.router.events
            .pipe(filter(event => event instanceof NavigationEnd))
            .subscribe((event: NavigationEnd) => {
              gtag('config', 'G-Z2ZRH8D5SC', {
                page_path: event.urlAfterRedirects
              });
            });

    }

  get maintenanceMode(): boolean {
    return this.loggerService.maintenanceMode;
  }

  get maintenancePlanning(): boolean {
    return this.loggerService.maintenancePlanning;
  }

  get maintenancePlanningTime(): { start?: string; end?: string } | null {
    return this.loggerService.maintenancePlanningTime;
  }

  get maintenanceMessage(): string {
    return this.loggerService.maintenanceMessage
      || 'Strona jest obecnie aktualizowana. Spróbuj ponownie później.';
  }

  get isCardRoute(): boolean {
    return typeof this.router.url === 'string' && this.router.url.startsWith('/karta');
  }

  get showMaintenanceRefresh(): boolean {
    const end = this.maintenancePlanningTime?.end;
    if (!end) {
      return false;
    }
    const endTime = Date.parse(end);
    return !Number.isNaN(endTime) && Date.now() > endTime;
  }

  refreshPage(): void {
    window.location.reload();
  }

  goTo(path: string) {
    this.router.navigate([path]);
  }

  onTabChange(event: any): void {
    const tabIndex = event.index;

    // Navigate based on the selected tab index
    switch (tabIndex) {
      case 0:
        this.goTo('/home');
        break;
      case 1:
        this.goTo('/news');
        break;
      case 2:
        this.goTo('/account');
        break;
      default:
        this.goTo('/home');
    }
  }

  ngAfterViewInit(): void {
    // Apply fallbacks after the view is fully initialized
    setTimeout(() => this.applyIconFallbacks(), 0);
    setTimeout(() => this.applyIconFallbacks(), 100);

  }

  ngOnInit(): void {
    localStorage.setItem('mobileApp', '1');
    // Load WCAG mode preference
    const savedWcagMode = localStorage.getItem('wcagMode');
    this.wcagMode = savedWcagMode === 'true';
    if (this.wcagMode) {
      this.applyWcagMode();
    }

    // Setup mock bridge for testing (remove in production)
    // this.mobileService.setupMockBridge(true);

    // Register mobile device with bridge information
   
    //


    /*
    const mobileDevice = this.mobileService.getDeviceInfo();
    if (mobileDevice?.device_id) {
      this.mobileService.register(mobileDevice).subscribe({
        next: () => console.log('[Mobile] Device registered successfully'),
        error: (err) => console.error('[Mobile] Registration failed:', err)
      });
    }
    */

    this.applyIconFallbacks();
    setTimeout(() => this.applyIconFallbacks(), 3000);

    // Set up a MutationObserver to apply fallbacks when new icons are added
    const observer = new MutationObserver(() => {
      setTimeout(() => this.applyIconFallbacks(), 100);
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true
    });

    this.router.events
      .pipe(filter((event: any) => event instanceof NavigationEnd))
      .subscribe(() => {
        this.updatePageTitle();
        this.updateBackButtonVisibility();

        // Scroll to top on every navigation unless ScrollPositionService
        // is restoring a saved position (returning from detail → list).
        if (!this.scrollPositionService.isScrollRestoring) {
          const appContent = document.querySelector('.app-content') as HTMLElement | null;
          if (appContent) {
            appContent.scrollTo({ top: 0, behavior: 'instant' });
          } else {
            window.scrollTo({ top: 0, behavior: 'instant' });
          }
        }
      });



    // Subscribe to PwaService for install prompt state (use showInstallInApp$ for app-level button)
    this.pwaService.showInstallInApp$.subscribe(shouldShow => {
      this.showInstallPwa = shouldShow;
      localStorage.setItem('showInstallPwa', shouldShow ? '1' : '0');
    });

    this.checkLoginStatus();
    
    this.userService.loggedIn.subscribe((loggedIn) => {
      this.isLoggedIn = loggedIn; // React to login state changes
    });

    // Listen for offline/online events with enhanced detection
    window.addEventListener('offline', () => {
      this.wasOffline = true;
      this.snackBar.open(
        'Brak połączenia z internetem. Pracujesz w trybie offline.',
        'Zamknij',
        {
          duration: 6000,
          panelClass: ['warning-snackbar'],
          horizontalPosition: 'center',
          verticalPosition: 'bottom',
        }
      );
      // Start polling for connectivity
      this.startConnectivityCheck();
    });

    window.addEventListener('online', () => {
      this.handleConnectionRestored();
    });

    // Initial check if already offline
    if (!navigator.onLine) {
      this.wasOffline = true;
      this.startConnectivityCheck();
    }

    setTimeout(() => {
      const scrollContainers = document.getElementsByClassName('app-content');
      if (scrollContainers.length > 0) {
        scrollContainers[0].addEventListener('scroll', this.onScroll, true);
      } else {
        window.addEventListener('scroll', this.onScroll, true);
      }
    }, 0);

    // Navigation logic is now handled after data loading in handlePostLoadingNavigation()

  }

  async promptPwaInstall() {
    if (this.pwaService.canInstall()) {
      const success = await this.pwaService.installPwa();
      if (!success) {
        alert('Aplikacja nie mogła zostać zainstalowana. Możesz spróbować ponownie później.');
      }
    } else {
      // Check if already installed
      if (window.matchMedia('(display-mode: standalone)').matches) {
        alert('Aplikacja jest już zainstalowana!');
        return;
      }
      
    }
  }

  goBack() {
    this.location.back();
  }

  showAppInfo() {
    const appInfo = this.appInfoService.getAppInfo();
    const dialogRef = this.dialog.open(AppInfoDialogComponent, {
      width: '90vw',
      maxWidth: '400px',
      data: {
        ...appInfo,
        version: this.loggerService.appVersion,
        buildNumber: this.loggerService.buildNumber,
      },
      panelClass: 'app-info-dialog-panel'
    });
  }

  toggleWcagMode() {
    this.wcagMode = !this.wcagMode;
    localStorage.setItem('wcagMode', this.wcagMode.toString());
    if (this.wcagMode) {
      this.applyWcagMode();
    } else {
      this.removeWcagMode();
    }
  }

  private applyWcagMode() {
    document.documentElement.classList.add('wcag-mode');
  }

  private removeWcagMode() {
    document.documentElement.classList.remove('wcag-mode');
  }  checkLoginStatus(): void {
    // Check if the user token exists in localStorage
    this.isLoggedIn = !!localStorage.getItem('userToken');
  }

  toggleTheme(): void {
    this.themeService.toggleTheme();
    // Re-apply icon fallbacks after theme change to ensure correct colors
    setTimeout(() => this.applyIconFallbacks(), 0);
    setTimeout(() => this.applyIconFallbacks(), 100);
  }

  logout(): void {
    // Remove the user tokens from localStorage
    this.userService.logoutUser();
    localStorage.clear();
    this.isLoggedIn = false;

    this.userService.loggedIn.next(false); // Update the loggedIn BehaviorSubject

    this.snackBar.open('Użytkownik został wylogowany', 'Zamknij', {
      duration: 4000,
      panelClass: ['success-snackbar'],
      horizontalPosition: 'center', // Center horizontally
      verticalPosition: 'bottom',
    });

    // Redirect to the home page or login page
    this.router.navigate(['/logowanie']);
  }

  private updatePageTitle(): void {
    const route = this.router.routerState.snapshot.root;
    const [title, intro] = this.getTitleFromRoute(route);
    this.pageTitle = title || '';
    this.pageIntro = intro || ''; // Update the intro text
    this.titleService.setTitle(`Zgrane Grono ${this.pageTitle.length > 1 ? '-' : ''} ${this.pageTitle}`); // Update the 
  }

  private getTitleFromRoute(route: any): [string | null, string | null] {
    let title = route.data?.['title'] || null;
    let intro = route.data?.['intro'] || null;
    if (route.firstChild) {
      [title, intro] = this.getTitleFromRoute(route.firstChild) || title;
    }
    return [title, intro];
  }

  ngOnDestroy() {
    const scrollContainers = document.getElementsByClassName('app-content');
    if (scrollContainers.length > 0) {
      scrollContainers[0].removeEventListener('scroll', this.onScroll, true);
    } else {
      window.removeEventListener('scroll', this.onScroll, true);
    }
    // Clear connectivity check interval
    if (this.connectivityCheckInterval) {
      clearInterval(this.connectivityCheckInterval);
    }
  }

  private startConnectivityCheck(): void {
    // Clear any existing interval
    if (this.connectivityCheckInterval) {
      clearInterval(this.connectivityCheckInterval);
    }
    
    // Check connectivity every 3 seconds
    this.connectivityCheckInterval = setInterval(() => {
      if (navigator.onLine && this.wasOffline) {

        this.handleConnectionRestored();
      }
    }, 3000);
  }

  private async handleConnectionRestored(): Promise<void> {
    if (!this.wasOffline) {
      return;
    }

    // Stop polling
    if (this.connectivityCheckInterval) {
      clearInterval(this.connectivityCheckInterval);
      this.connectivityCheckInterval = null;
    }
  
    // Try to update service worker
    if ('serviceWorker' in navigator) {
      try {
        const registration = await navigator.serviceWorker.getRegistration();
        if (registration) {
          await registration.update();
        }
      } catch (error) {
        console.error('Error updating service worker:', error);
      }
    }

    this.snackBar.open(
      'Połączenie przywrócone. Odświeżanie aplikacji...',
      '',
      {
        duration: 1500,
        panelClass: ['success-snackbar'],
        horizontalPosition: 'center',
        verticalPosition: 'bottom',
      }
    );
    
    // Reset flag and hard reload to bypass cache
    this.wasOffline = false;
    setTimeout(() => {
      // Force hard reload to bypass all caches
      window.location.href = window.location.href;
    }, 1500);
  }

  onScroll = () => {
    const scrollContainers = document.getElementsByClassName('app-content');
    const scrollY = scrollContainers.length > 0
      ? (scrollContainers[0] as HTMLElement).scrollTop
      : window.scrollY;
    this.showGoTop = scrollY > 100;
  };

  scrollToTop() {
    const scrollContainers = document.getElementsByClassName('app-content');
    if (scrollContainers.length > 0) {
      (scrollContainers[0] as HTMLElement).scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }

  private applyIconFallbacks(): void {
    // Check if Material Icons font is loaded
    const testElement = document.createElement('span');
    testElement.style.fontFamily = 'Material Icons';
    testElement.style.position = 'absolute';
    testElement.style.left = '-9999px';
    testElement.textContent = 'home';
    document.body.appendChild(testElement);
    
    const computedFont = window.getComputedStyle(testElement).fontFamily;
    const isFontLoaded = computedFont.includes('Material Icons');
    document.body.removeChild(testElement);
    
    // Avoid repeated logs if state has not changed
    if (this.lastIconsLoadedState === isFontLoaded) {
      return;
    }
    this.lastIconsLoadedState = isFontLoaded;

    if (!isFontLoaded) {
      // Add class to activate SVG fallbacks
      document.body.classList.add('material-icons-fallback');
    } else {
      document.body.classList.remove('material-icons-fallback');
    }
  }

  private updateBackButtonVisibility(): void {
    const currentUrl = this.router.url;
    
    // Don't show back button for offer category pages like /oferty/categoryname
    if (currentUrl.startsWith('/oferty/') && currentUrl.split('/').length === 3) {
      this.showGoBack = false;
      return;
    }
    
    // Check if the URL has double slashes (e.g., /oferty/something, /wiadomosci/something, /partner/something)
    const urlParts = currentUrl.split('/').filter(part => part.length > 0);
    this.showGoBack = urlParts.length > 1 || currentUrl.includes('/person-edit');
  }

  // Splash screen methods
  onSplashComplete(): void {
    // Always hide splash screen immediately when completion is triggered
    this.showSplashScreen = false;
  }

  private startDataLoading(): void {
    this.isLoadingData = true;
    const startTime = Date.now();
    
    // Array of data loading tasks
    const loadingTasks = [];
    
    // Load user data if logged in
    if (this.isLoggedIn || localStorage.getItem('userToken')) {
      // Load current user role and permissions
      loadingTasks.push(
        this.userService.getRole().pipe(
          catchError(error => {
            return of(null);
          })
        )
      );
      
      // You can add more data loading tasks here:
      // loadingTasks.push(this.newsService.getLatestNews());
      // loadingTasks.push(this.offersService.getUserOffers());
      // loadingTasks.push(this.cardsService.getUserCards());
    }
    
    // Always load basic app data
    loadingTasks.push(
      // Preload theme settings and basic app initialization
      of(this.themeService.getCurrentTheme()).pipe(
        catchError(() => of(null))
      )
    );
    
    // Add artificial minimum loading time for smooth UX
    loadingTasks.push(
      of('initialization').pipe(
        catchError(() => of(null))
      )
    );
    
    // Execute all loading tasks
    forkJoin(loadingTasks.length > 0 ? loadingTasks : [of(null)])
      .subscribe(
        (results) => {
          this.handleDataLoadingComplete(startTime);
        },
        (error) => {
          console.error('Error during data loading:', error);
          this.handleDataLoadingComplete(startTime);
        }
      );
  }
  
  private handleDataLoadingComplete(startTime: number): void {
    const elapsedTime = Date.now() - startTime;
    const remainingTime = Math.max(0, this.minimumSplashTime - elapsedTime);
    
    // Ensure minimum time has passed for branding
    setTimeout(() => {
      this.isLoadingData = false;
      this.loadingProgress = 100;
      
      // Auto-close splash screen if still showing (not clicked)
      if (this.showSplashScreen && this.splashScreenComponent) {
        this.splashScreenComponent.completeAfterDataLoading();
      }
      
      // Navigate to appropriate page after data loading
      this.handlePostLoadingNavigation();
    }, remainingTime);
  }
  
  private handlePostLoadingNavigation(): void {
    // Redirect to /karta if first load and user is logged in
    if (this.isLoggedIn || localStorage.getItem('userToken')) {
      if (window.location.pathname === '/' || window.location.pathname === '/home') {
        this.router.navigate(['/karta']);
        return;
      }
    }
  }

}